import { useCallback, useRef } from 'react';

import type { DraftField } from '../components/table/node-property-panel/ai-action-assist/action-assist-model';
import { bobcatApiUrl } from '../utils/bobcat-api';

/**
 * Calls the `/ai/action-assist/*` routes the backend serves (`bobcatApiUrl`;
 * its `bobcat/src/server-only/ai/router.ts`).
 */

export interface ActionBriefInput {
  scriptName: string;
  purpose: string;
  parameterInput: string[];
  globals: string[];
  details: string;
  outputVariables: string[];
  outcomes: string;
}

export interface ActionDraft {
  summary: string;
  fields: DraftField[];
  reply?: string;
}

export interface ChatAnswer {
  answer: string;
  suggestBuild: boolean;
  /** Proposed node content — validated client-side before it is shown. */
  proposals?: {
    nodeNumber: string;
    fields: { column: string; value: string; why: string }[];
  }[];
  /** Agent-setting changes, each to an allowed value (`agent-settings`). */
  settingChanges?: {
    agent: 'supervisor' | 'knowledge';
    key: string;
    to: string | boolean;
  }[];
  /** A question it asks instead of guessing. */
  ask?: { question: string; why: string; options: string[] };
}

/** Above the server function's 60s limit would be pointless; a little under
 *  it means the browser says so rather than the platform. */
const TIMEOUT_MS = 280_000;

const post = async <T>(
  path: string,
  body: unknown,
  signal: AbortSignal
): Promise<T> => {
  const response = await fetch(bobcatApiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    const b = json as { detail?: string; error?: string } | null;
    throw new Error(b?.detail ?? b?.error ?? response.statusText);
  }
  return json as T;
};

/** Thrown when the designer pressed Stop — not an error to show. */
export class ActionAssistAborted extends Error {}

export const useActionAssist = () => {
  const controller = useRef<AbortController | null>(null);

  const run = useCallback(async <T>(path: string, body: unknown) => {
    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      c.abort();
    }, TIMEOUT_MS);
    try {
      return await post<T>(path, body, c.signal);
    } catch (err) {
      if (c.signal.aborted) {
        if (timedOut) {
          throw new Error(
            'The assistant took too long to respond. Try again, or shorten the brief.'
          );
        }
        throw new ActionAssistAborted();
      }
      throw err instanceof Error ? err : new Error(String(err));
    } finally {
      clearTimeout(timer);
      if (controller.current === c) controller.current = null;
    }
  }, []);

  const draft = useCallback(
    (body: {
      brief: ActionBriefInput;
      context: string;
      nodeNumber: string;
      commands: string[];
      previous?: DraftField[];
      request?: string;
    }) => run<ActionDraft>('/ai/action-assist/draft', body),
    [run]
  );

  const ask = useCallback(
    (body: {
      question: string;
      context: string;
      /** The selected node, when the question is about one. */
      nodeNumber?: string;
      history: { who: 'me' | 'ai'; text: string }[];
      /** Commands this version has, so a proposal never invents one. */
      commands?: string[];
      /** The two core agents' settings, so it can propose changing them. */
      agentSettings?: {
        supervisor: Record<string, string | boolean>;
        knowledge: Record<string, string | boolean>;
      };
    }) => run<ChatAnswer>('/ai/action-assist/chat', body),
    [run]
  );

  const stop = useCallback(() => controller.current?.abort(), []);

  return { draft, ask, stop };
};
